import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, TextField, RadioGroup, FormControlLabel,
  Radio, Checkbox, FormGroup, FormControl, Select, MenuItem,
  Rating, Slider, Button, CircularProgress, Alert, Container
} from '@mui/material';
import { CloudUpload, Send, CheckCircle } from '@mui/icons-material';
import axios from 'axios';
import { useParams, useSearchParams } from 'react-router-dom';

const PublicFormView = () => {
  const { db, formId } = useParams();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [responses, setResponses] = useState({});
  const [email, setEmail] = useState('');

  // Get customer info from URL params (if sent via email)
  const customerId = searchParams.get('cid');
  const customerName = searchParams.get('cname');
  const customerMobile = searchParams.get('cmobile');

  // Handle case where db might actually be the formId (old URL format)
  const actualDb = formId ? db : null;
  const actualFormId = formId || db;

  useEffect(() => {
    if (!actualDb) {
      setError('Invalid form link. Please request a new link from the sender.');
      setLoading(false);
      return;
    }
    if (actualDb && actualFormId) {
      fetchForm();
    }
  }, [actualDb, actualFormId]);

  const fetchForm = async () => {
    try {
      const res = await axios.get(`/api/crm-forms/public/${actualDb}/${actualFormId}`);
      if (res.status === 200 && res.data.data) {
        setForm(res.data.data);
        // Initialize responses
        const initialResponses = {};
        res.data.data.fields?.forEach(field => {
          if (field.type === 'CHECKBOX') {
            initialResponses[field.id] = [];
          } else {
            initialResponses[field.id] = '';
          }
        });
        setResponses(initialResponses);
      } else {
        setError('Form not found');
      }
    } catch (err) {
      console.error('Error fetching form:', err);
      setError(err.response?.data?.data || 'Failed to load form');
    } finally {
      setLoading(false);
    }
  };

  const handleResponseChange = (fieldId, value) => {
    setResponses(prev => ({ ...prev, [fieldId]: value }));
  };

  const handleCheckboxChange = (fieldId, option, checked) => {
    setResponses(prev => {
      const current = prev[fieldId] || [];
      if (checked) {
        return { ...prev, [fieldId]: [...current, option] };
      } else {
        return { ...prev, [fieldId]: current.filter(o => o !== option) };
      }
    });
  };

  const handleSubmit = async () => {
    // Validate required fields
    const missingRequired = form.fields?.filter(field => 
      field.required && !responses[field.id] && 
      (field.type !== 'CHECKBOX' || responses[field.id]?.length === 0)
    );

    if (missingRequired?.length > 0) {
      setError(`Please fill in all required fields: ${missingRequired.map(f => f.label).join(', ')}`);
      return;
    }

    // Only require email if form needs it AND customer not already identified
    if (form.settings?.collectEmail && !email && !customerId) {
      setError('Please enter your email address');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const payload = {
        formId: form._id,
        responses: form.fields?.map(field => ({
          fieldId: field.id,
          fieldLabel: field.label,
          fieldType: field.type,
          value: responses[field.id]
        })),
        respondentEmail: email || null,
        submittedAt: new Date().toISOString(),
        // Include customer info from URL params
        customerId: customerId || null,
        customerName: customerName || null,
        customerMobile: customerMobile || null
      };

      const res = await axios.post(`/api/crm-forms/public/${actualDb}/submit`, payload);
      
      if (res.status === 200 || res.data.status === 200) {
        setSubmitted(true);
      } else {
        throw new Error(res.data?.data || 'Failed to submit');
      }
    } catch (err) {
      console.error('Submit error:', err);
      setError(err.response?.data?.data || 'Failed to submit form');
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field) => {
    switch (field.type) {
      case 'SHORT_TEXT':
        return (
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Your answer"
            value={responses[field.id] || ''}
            onChange={(e) => handleResponseChange(field.id, e.target.value)}
          />
        );
      
      case 'LONG_TEXT':
        return (
          <TextField
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            placeholder="Your answer"
            value={responses[field.id] || ''}
            onChange={(e) => handleResponseChange(field.id, e.target.value)}
          />
        );
      
      case 'MULTIPLE_CHOICE':
        return (
          <RadioGroup
            value={responses[field.id] || ''}
            onChange={(e) => handleResponseChange(field.id, e.target.value)}
          >
            {field.options?.map((opt, i) => (
              <FormControlLabel key={i} value={opt} control={<Radio />} label={opt} />
            ))}
          </RadioGroup>
        );
      
      case 'CHECKBOX':
        return (
          <FormGroup>
            {field.options?.map((opt, i) => (
              <FormControlLabel
                key={i}
                control={
                  <Checkbox
                    checked={responses[field.id]?.includes(opt) || false}
                    onChange={(e) => handleCheckboxChange(field.id, opt, e.target.checked)}
                  />
                }
                label={opt}
              />
            ))}
          </FormGroup>
        );
      
      case 'DROPDOWN':
        return (
          <FormControl fullWidth variant="outlined">
            <Select
              value={responses[field.id] || ''}
              onChange={(e) => handleResponseChange(field.id, e.target.value)}
              displayEmpty
            >
              <MenuItem value="">Choose an option</MenuItem>
              {field.options?.map((opt, i) => (
                <MenuItem key={i} value={opt}>{opt}</MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      
      case 'RATING':
        return (
          <Rating
            size="large"
            value={Number(responses[field.id]) || 0}
            onChange={(e, value) => handleResponseChange(field.id, value)}
          />
        );
      
      case 'SCALE':
        return (
          <Box sx={{ px: 2, pt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="caption">{field.settings?.scaleMinLabel || field.settings?.scaleMin || 1}</Typography>
              <Typography variant="caption">{field.settings?.scaleMaxLabel || field.settings?.scaleMax || 5}</Typography>
            </Box>
            <Slider
              min={field.settings?.scaleMin || 1}
              max={field.settings?.scaleMax || 5}
              marks
              valueLabelDisplay="auto"
              value={Number(responses[field.id]) || field.settings?.scaleMin || 1}
              onChange={(e, value) => handleResponseChange(field.id, value)}
            />
          </Box>
        );
      
      case 'DATE':
        return (
          <TextField
            type="date"
            variant="outlined"
            value={responses[field.id] || ''}
            onChange={(e) => handleResponseChange(field.id, e.target.value)}
            sx={{ width: 250 }}
            InputLabelProps={{ shrink: true }}
          />
        );
      
      case 'TIME':
        return (
          <TextField
            type="time"
            variant="outlined"
            value={responses[field.id] || ''}
            onChange={(e) => handleResponseChange(field.id, e.target.value)}
            sx={{ width: 200 }}
            InputLabelProps={{ shrink: true }}
          />
        );
      
      case 'NUMBER':
        return (
          <TextField
            type="number"
            variant="outlined"
            placeholder="0"
            value={responses[field.id] || ''}
            onChange={(e) => handleResponseChange(field.id, e.target.value)}
            sx={{ width: 200 }}
          />
        );
      
      case 'EMAIL':
        return (
          <TextField
            type="email"
            variant="outlined"
            placeholder="email@example.com"
            value={responses[field.id] || ''}
            onChange={(e) => handleResponseChange(field.id, e.target.value)}
            fullWidth
          />
        );
      
      case 'PHONE':
        return (
          <TextField
            type="tel"
            variant="outlined"
            placeholder="+91 98765 43210"
            value={responses[field.id] || ''}
            onChange={(e) => handleResponseChange(field.id, e.target.value)}
            sx={{ width: 250 }}
          />
        );
      
      case 'FILE':
        return (
          <Button variant="outlined" startIcon={<CloudUpload />} component="label">
            Upload File
            <input type="file" hidden onChange={(e) => handleResponseChange(field.id, e.target.files[0]?.name)} />
          </Button>
        );
      
      default:
        return (
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Your answer"
            value={responses[field.id] || ''}
            onChange={(e) => handleResponseChange(field.id, e.target.value)}
          />
        );
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#f0ebf8' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !form) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#f0ebf8' }}>
        <Paper sx={{ p: 4, textAlign: 'center', maxWidth: 400 }}>
          <Typography variant="h6" color="error" gutterBottom>Form Not Available</Typography>
          <Typography color="textSecondary">{error}</Typography>
        </Paper>
      </Box>
    );
  }

  if (submitted) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#f0ebf8' }}>
        <Paper sx={{ p: 5, textAlign: 'center', maxWidth: 500, borderTop: `10px solid ${form?.themeColor || '#673ab7'}` }}>
          <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" fontWeight={500} gutterBottom>
            {form?.settings?.confirmationMessage || 'Thank you for your response!'}
          </Typography>
          <Typography color="textSecondary">
            Your response has been recorded.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f0ebf8', py: 4 }}>
      <Container maxWidth="md">
        {/* Form Header */}
        <Paper 
          sx={{ 
            borderTop: `10px solid ${form?.themeColor || '#673ab7'}`,
            borderRadius: 2,
            overflow: 'hidden',
            mb: 2
          }}
        >
          {form?.headerImage && (
            <Box
              component="img"
              src={form.headerImage}
              alt="Form header"
              sx={{ width: '100%', height: 200, objectFit: 'cover' }}
            />
          )}
          <Box sx={{ p: 3 }}>
            <Typography variant="h4" fontWeight={500}>
              {form?.title || 'Untitled Form'}
            </Typography>
            {form?.description && (
              <Typography color="textSecondary" sx={{ mt: 1 }}>
                {form.description}
              </Typography>
            )}
            {/* Only show email field if: 1) form requires it AND 2) customer info not already in URL */}
            {form?.settings?.collectEmail && !customerId && (
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #eee' }}>
                <Typography variant="body2" color="error" sx={{ mb: 1 }}>
                  * Required
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Email"
                  placeholder="Your email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  type="email"
                />
              </Box>
            )}
            {/* Show greeting if customer is known */}
            {customerName && (
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #eee' }}>
                <Typography variant="body2" color="textSecondary">
                  Responding as: <strong>{customerName}</strong>
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Questions */}
        {form?.fields?.map((field, index) => (
          <Paper key={field.id || index} sx={{ p: 3, mb: 2, borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight={500} sx={{ mb: 1 }}>
              {field.label || 'Untitled Question'}
              {field.required && <span style={{ color: '#d32f2f' }}> *</span>}
            </Typography>
            
            {field.description && (
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                {field.description}
              </Typography>
            )}

            {renderField(field)}
          </Paper>
        ))}

        {/* Submit Button */}
        {form?.fields?.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button
              variant="contained"
              size="large"
              startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <Send />}
              onClick={handleSubmit}
              disabled={submitting}
              sx={{ 
                bgcolor: form?.themeColor || '#673ab7',
                '&:hover': { bgcolor: form?.themeColor || '#673ab7', filter: 'brightness(0.9)' }
              }}
            >
              {submitting ? 'Submitting...' : 'Submit'}
            </Button>
            <Typography variant="caption" color="textSecondary">
              Never submit passwords through this form
            </Typography>
          </Box>
        )}

        {/* Empty State */}
        {(!form?.fields || form.fields.length === 0) && (
          <Paper sx={{ p: 5, textAlign: 'center' }}>
            <Typography color="textSecondary">
              This form has no questions.
            </Typography>
          </Paper>
        )}
      </Container>
    </Box>
  );
};

export default PublicFormView;
